import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Dropdown, IDropdownOption } from "@fluentui/react";
import { useTranslation } from "react-i18next";
import { useSelector } from "../../../store/hooks";
import { SerializedTaxonomyFilter } from "../../../store/tableFilters";
import { TaxonomyKey } from "../../../store/taxonomy/taxonomy";
import styled from "styled-components";

type TaxonomyFilterSelectProps = {
  value: SerializedTaxonomyFilter;
  onChange: (filter: SerializedTaxonomyFilter) => void;
  taxonomyKey: string;
  exclude?: string[];
  customOptionLabel?: (tax: any) => string;
  multiSelect?: boolean;
};

export const TaxonomyFilterSelect = ({
  value,
  onChange,
  taxonomyKey,
  exclude = [],
  customOptionLabel,
  multiSelect = true,
}: TaxonomyFilterSelectProps) => {
  const { t } = useTranslation();
  const taxonomy = useSelector((state) => state.taxonomy);
  const [selectedKeys, setSelectedKeys] = useState<(string | number)[]>(
    value.value.map(String),
  );

  useEffect(() => {
    setSelectedKeys(value.value.map(String));
  }, [value.value]);

  const options: IDropdownOption[] = useMemo(() => {
    const taxKey = taxonomyKey as TaxonomyKey;
    const taxonomyData = taxonomy[taxKey];

    if (!taxonomyData?.items || taxonomyData.items.length === 0) {
      return [];
    }

    return taxonomyData.items
      .filter((tax) => {
        return tax && !exclude.includes(tax.code);
      })
      .map((tax) => {
        const label = customOptionLabel ? customOptionLabel(tax) : t(tax.code);
        return {
          key: String(tax.id),
          text: label,
        };
      });
  }, [taxonomy, taxonomyKey, exclude, customOptionLabel, t]);

  const handleChange = useCallback(
    (
      event: React.FormEvent<HTMLDivElement>,
      option?: IDropdownOption,
      index?: number,
    ) => {
      if (!option) return;

      let newSelectedKeys: (string | number)[];

      if (multiSelect) {
        if (option.selected) {
          newSelectedKeys = [...selectedKeys, option.key];
        } else {
          newSelectedKeys = selectedKeys.filter((k) => k !== option.key);
        }
      } else {
        newSelectedKeys = [option.key];
      }

      setSelectedKeys(newSelectedKeys);
      onChange({
        type: "taxonomy",
        taxonomyKey,
        value: newSelectedKeys.map(Number),
      });
    },
    [selectedKeys, taxonomyKey, multiSelect, onChange],
  );

  return (
    <Container>
      <Dropdown
        multiSelect={multiSelect}
        selectedKeys={selectedKeys as string[]}
        options={options}
        onChange={handleChange}
        placeholder={t("ram.table.filter.selectValues")}
        styles={{
          root: { width: "100%" },
          dropdown: { borderRadius: 4 },
          dropdownItemsWrapper: { maxHeight: 300 },
        }}
      />
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
`;

export default TaxonomyFilterSelect;
